import { useIntl } from 'react-intl';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSearchCardsContext } from '@sorare/core/src/contexts/searchCards';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';
import type { VirtualToggleFilter } from '@sorare/core/src/lib/filters';
import {
  TOGGLE_FILTERS,
  VIRTUAL_TOGGLE_FILTERS,
} from '@sorare/core/src/lib/filters';

import { useFormatFilters } from 'hooks/filters/useFormatFilters';
import { useFormatFloorPricesFilters } from 'hooks/filters/useFormatFloorPricesFilters';
import type { RefinementItem } from 'search/ActiveFilters/types';

import { useFormatActiveFixtureLabel } from '../useFormatActiveFixtureLabel';
import {
  formatListFilterWidgetValue,
  formatRangeFilterWidgetValue,
} from './utils';

export const isListFilterWidget = (item: RefinementItem): boolean =>
  item.refinements[0]?.type === 'disjunctive';

export const isRangeFilterWidget = (item: RefinementItem): boolean =>
  (item.refinements.length === 1 || item.refinements.length === 2) &&
  item.refinements.some(e => e.type === 'numeric');

export const useFormatFilterWidgetValue = () => {
  const { formatMessage } = useIntl();
  const { formatPrice } = useFormatFilters();
  const { formatPrice: formatFloorPrices } = useFormatFloorPricesFilters();
  const {
    competitionEligibilityFilter,
    customDecksFilter,
    gameweekFilter,
    dailyFilter,
    probablePitchersFilter,
    promotionalEvent,
    watchlistFilter,
  } = useSearchCardsContext();
  const { promotionalEvents } = useConfigContext();
  const { filterableSo5Competitions } = useSo5Competitions();
  const { language } = useIntlContext();
  const formatActiveFixtureLabel = useFormatActiveFixtureLabel();

  const formatFilterWidgetValue = (
    item: RefinementItem | VirtualToggleFilter
  ): string[] => {
    const isRefinementItem = 'attribute' in item;

    if (isRefinementItem) {
      const toggleFilter = Object.values(TOGGLE_FILTERS).find(
        f => f.attribute === item.attribute
      );
      if (toggleFilter) {
        return [
          typeof toggleFilter.label === 'string'
            ? toggleFilter.label
            : formatMessage(toggleFilter.label),
        ];
      }
      if (isListFilterWidget(item)) {
        return formatListFilterWidgetValue(item, {
          formatMessage,
          language,
        });
      }
      if (isRangeFilterWidget(item)) {
        return formatRangeFilterWidgetValue(item, {
          formatPrice,
          formatFloorPrices,
        });
      }
    } else {
      if (item.name === VIRTUAL_TOGGLE_FILTERS.watchlistFilter.name) {
        const { title = '' } = watchlistFilter || {};
        return [
          `${formatMessage(VIRTUAL_TOGGLE_FILTERS.watchlistFilter.label)}: ${title}`,
        ];
      }
      if (
        item.name === VIRTUAL_TOGGLE_FILTERS.competitionEligibilityFilter.name
      ) {
        return [
          filterableSo5Competitions.find(
            so5Competition =>
              so5Competition.slug === competitionEligibilityFilter
          )?.displayName || '',
        ];
      }
      if (item.name === VIRTUAL_TOGGLE_FILTERS.gameweekFilter.name) {
        return [
          formatActiveFixtureLabel(gameweekFilter, So5FixtureEvent.CLASSIC),
        ];
      }
      if (item.name === VIRTUAL_TOGGLE_FILTERS.dailyFilter.name) {
        return [formatActiveFixtureLabel(dailyFilter, So5FixtureEvent.DAILY)];
      }

      if (item.name === VIRTUAL_TOGGLE_FILTERS.probablePitchersFilter.name) {
        return [
          `${formatMessage(
            VIRTUAL_TOGGLE_FILTERS.probablePitchersFilter.title
          )} - GW ${probablePitchersFilter}` || '',
        ];
      }
      if (item.name === VIRTUAL_TOGGLE_FILTERS.customDecksFilter.name) {
        return [customDecksFilter || ''];
      }
      if (item.name === VIRTUAL_TOGGLE_FILTERS.promotionalEventFilter.name) {
        return [
          promotionalEvents.find(p => p.slug === promotionalEvent)?.name || '',
        ];
      }
      return ['label' in item && item.label ? formatMessage(item.label) : ''];
    }

    return [''];
  };

  return {
    formatFilterWidgetValue,
  };
};
